/**
 * Loads a theme's web fonts on demand. Every surface (editor canvas, preview,
 * published site) renders through DocumentRenderer, so fonts are requested
 * there instead of being hard-coded in each app's HTML.
 */

/** Google Fonts family name and the weights the renderer uses, per font id. */
const GOOGLE_FONTS: Record<string, { family: string; weights: string }> = {
  inter: { family: "Inter", weights: "400;500;600;700;800" },
  roboto: { family: "Roboto", weights: "400;500;700" },
  "open-sans": { family: "Open Sans", weights: "400;600;700;800" },
  lato: { family: "Lato", weights: "400;700" },
  montserrat: { family: "Montserrat", weights: "400;500;600;700;800" },
  "dm-sans": { family: "DM Sans", weights: "400;500;600;700;800" },
  "space-grotesk": { family: "Space Grotesk", weights: "400;500;600;700" },
  "ibm-plex-sans": { family: "IBM Plex Sans", weights: "400;500;600;700" },
  "playfair-display": { family: "Playfair Display", weights: "400;500;600;700;800" },
  merriweather: { family: "Merriweather", weights: "400;700" },
  "source-serif-4": { family: "Source Serif 4", weights: "400;600;700" },
  "libre-baskerville": { family: "Libre Baskerville", weights: "400;700" },
};

/** Stylesheet URL for a font id, or undefined for unknown ids. */
export function fontStylesheetUrl(fontId: string): string | undefined {
  const font = GOOGLE_FONTS[fontId];
  if (!font) return undefined;
  const family = font.family.replace(/ /g, "+");
  return `https://fonts.googleapis.com/css2?family=${family}:wght@${font.weights}&display=swap`;
}

/** Adds a stylesheet link for each font once per page; safe to call often. */
export function ensureFonts(fontIds: Array<string | undefined>): void {
  if (typeof document === "undefined") return;
  for (const id of new Set(fontIds)) {
    if (!id) continue;
    const href = fontStylesheetUrl(id);
    if (!href) continue;
    const marker = `zirro-font-${id}`;
    if (document.getElementById(marker)) continue;
    const link = document.createElement("link");
    link.id = marker;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
}
